<template>
  <div class="addFormNotesTab">
    <section class="addFormNotesTab__notesToStaff mb-15px">
      <RichTextEditor
        :title="'Note to Staff'"
        :isReadOnly="!canEditNotes"
        v-model="noteToStaff" />
    </section>
    <section class="addFormNotesTab__notesToManagers mb-15px" v-if="canEditNotes">
      <RichTextEditor
        :title="'Note to Managers'"
        v-model="noteToManagers" />
    </section>
    <section class="addFormNotesTab__clientRequest mb-15px" v-if="canEditNotes">
      <RichTextEditor
        :title="'Client Request | Source Text'"
        v-model="clientRequest" />
    </section>
    <div class="addFormNotesTab__delimetr" v-if="canEditNotes"></div>
    <section class="addFormNotesTab__links">
      <div
        v-for="(link, index) in links"
        :key="`link-${index}`"
        class="addFormNotesTab__item"
      >
        <v-icon class="addFormNotesTab__item--icon" size="18">mdi-link-variant</v-icon>
        <a
          class="addFormNotesTab__item--name"
          :href="normalizeLink(link)"
          target="_blank"
          rel="noopener noreferrer"
        >{{ link }}</a>
        <span
          class="addFormNotesTab__item--remove"
          title="Remove link"
          @click.stop.prevent="removeLink(index)"
        >
          <pmdashboard_table_icons
            :icon="'pmdashboard_clear_text_field'"
            class="pmdashboard__clear-text d-block"
          />
        </span>
      </div>
    </section>
    <section class="addFormNotesTab__files">
      <div
        v-for="(document, index) in documents"
        :key="documentKey(document, index)"
        class="addFormNotesTab__item"
      >
        <v-icon class="addFormNotesTab__item--icon" size="18">mdi-file-document-outline</v-icon>
        <!-- The name is the download control, the same way a link row's name is
        the anchor. -->
        <span
          class="addFormNotesTab__item--name addFormNotesTab__item--name-action"
          :title="`Download ${document.fileName}`"
          @click.stop.prevent="downloadDocument(document, index)"
        >{{ document.fileName }}</span>
        <v-progress-circular
          v-if="isDocumentBusy(document, index)"
          indeterminate
          size="12"
          width="2"
          color="#1976D2"
        />
        <span class="addFormNotesTab__item--meta">{{ accessTypeLabel(document.fileAccessType) }}</span>
        <span v-if="!document.uploaded" class="addFormNotesTab__item--meta">· uploads on save</span>
        <span
          class="addFormNotesTab__item--remove"
          title="Remove document"
          @click.stop.prevent="removeDocument(document)"
        >
          <pmdashboard_table_icons
            :icon="'pmdashboard_clear_text_field'"
            class="pmdashboard__clear-text d-block"
          />
        </span>
      </div>
    </section>
    <section class="addFormNotesTab__action" v-if="canEditNotes">
      <v-btn
        depressed
        class="addFormNotesTab__action--button"
        color="#1976D2"
        @click="openDocumentsDialog"
      >
        <v-icon
          left
          color="#fff"
        >
          mdi-plus
        </v-icon>
        Add documents
      </v-btn>
      <v-btn
        depressed
        class="addFormNotesTab__action--button"
        color="#1976D2"
        @click="openLinkDialog"
      >
        <v-icon
          left
          color="#fff"
        >
          mdi-plus
        </v-icon>
        Add Link
      </v-btn>
    </section>

    <!-- add documents -->
    <v-dialog v-model="documentsDialog" max-width="520" @click:outside="closeDocumentsDialog">
      <v-card class="addFormNotesTab__dialog">
        <div class="addFormNotesTab__dialog--title">
          <p>Add documents</p>
          <v-btn icon small @click="closeDocumentsDialog">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="addFormNotesTab__dialog--body">
          <!-- Both the drop target and the picker collect into `stagedFiles`, so
          files can be gathered over several drops / browses before adding. -->
          <div
            class="addFormNotesTab__dropzone"
            :class="{ 'addFormNotesTab__dropzone--active': isDragOver }"
            @dragenter.prevent="isDragOver = true"
            @dragover.prevent="isDragOver = true"
            @dragleave.prevent="isDragOver = false"
            @drop.prevent="onDrop"
            @click="browseFiles"
          >
            <v-icon size="28" color="#1976D2">mdi-cloud-upload-outline</v-icon>
            <p class="addFormNotesTab__dropzone--text mb-0">
              Drop files here or <span class="addFormNotesTab__dropzone--link">browse</span>
            </p>
            <p class="addFormNotesTab__dropzone--hint mb-0">
              Select as many files as you need — repeat to keep adding.
            </p>
            <input
              ref="filePicker"
              type="file"
              multiple
              class="addFormNotesTab__dropzone--input"
              @change="onPick"
            />
          </div>
          <div v-if="stagedFiles.length" class="addFormNotesTab__staged">
            <div class="addFormNotesTab__staged--header">
              <span>{{ stagedFiles.length }} file{{ stagedFiles.length > 1 ? 's' : '' }} selected</span>
              <v-btn text x-small color="#1976D2" @click="clearStagedFiles">Clear all</v-btn>
            </div>
            <div class="addFormNotesTab__staged--list">
              <div
                v-for="(file, index) in stagedFiles"
                :key="`staged-${index}-${file.name}`"
                class="addFormNotesTab__staged--item"
              >
                <v-icon size="16" color="#656F7D">mdi-file-document-outline</v-icon>
                <span class="addFormNotesTab__staged--name">{{ file.name }}</span>
                <span class="addFormNotesTab__staged--size">{{ formatFileSize(file.size) }}</span>
                <v-btn
                  icon
                  x-small
                  class="addFormNotesTab__staged--remove"
                  title="Remove file"
                  @click.stop.prevent="removeStagedFile(index)"
                >
                  <v-icon size="14">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
          <v-select
            v-model="accessTypeToAdd"
            class="mt-4"
            :items="fileAccessTypes"
            item-text="label"
            item-value="value"
            filled
            hide-details
            label="Visibility"
          ></v-select>
          <p class="addFormNotesTab__dialog--hint mb-0 mt-3">
            Files are kept with the assignment and uploaded when you save it.
          </p>
        </div>
        <div class="addFormNotesTab__dialog--actions">
          <v-btn text @click="closeDocumentsDialog">Cancel</v-btn>
          <v-btn
            depressed
            color="#1976D2"
            class="addFormNotesTab__action--button"
            :disabled="!stagedFiles.length"
            @click="addDocuments"
          >
            Add{{ stagedFiles.length ? ` ${stagedFiles.length} file${stagedFiles.length > 1 ? 's' : ''}` : '' }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- add link -->
    <v-dialog v-model="linkDialog" max-width="520" @click:outside="closeLinkDialog">
      <v-card class="addFormNotesTab__dialog">
        <div class="addFormNotesTab__dialog--title">
          <p>Add link</p>
          <v-btn icon small @click="closeLinkDialog">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="addFormNotesTab__dialog--body">
          <v-text-field
            ref="linkInput"
            v-model="linkToAdd"
            filled
            label="Link"
            placeholder="https://"
            prepend-inner-icon="mdi-link-variant"
            :error-messages="linkError"
            @input="linkError = ''"
            @keydown.enter.prevent="addLink"
          ></v-text-field>
        </div>
        <div class="addFormNotesTab__dialog--actions">
          <v-btn text @click="closeLinkDialog">Cancel</v-btn>
          <v-btn
            depressed
            color="#1976D2"
            class="addFormNotesTab__action--button"
            :disabled="!linkToAdd.trim()"
            @click="addLink"
          >
            Add
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import RichTextEditor from '../../ui/RichTextEditor.vue';
import pmdashboard_table_icons from "@/assets/icons/pmdashboard_table_icons.vue";
import {
  FILE_ACCESS_TYPES,
  DEFAULT_FILE_ACCESS_TYPE,
  getFileAccessTypeLabel,
  createPendingDocument,
  downloadAssignmentDocument
} from "@/utils/assignmentDocumentUtils";

export default {
  name: 'ADFormNotesAndFilesTab',

  components: {
    RichTextEditor,
    pmdashboard_table_icons
  },

  data() {
    return {
      // `noteToStaff` is backed by the assignment's `note` field (see the
      // computed below). These two have no field on the assignment record yet, so
      // they stay local and are NOT saved.
      noteToManagers: '',
      clientRequest: '',
      fileAccessTypes: FILE_ACCESS_TYPES,
      documentsDialog: false,
      linkDialog: false,
      // Files collected in the dialog, added to the assignment on confirm.
      stagedFiles: [],
      accessTypeToAdd: DEFAULT_FILE_ACCESS_TYPE,
      isDragOver: false,
      linkToAdd: '',
      linkError: '',
      // Keys of the documents currently being downloaded, so only that row's
      // spinner runs while the rest stay usable.
      busyDocuments: []
    }
  },

  computed: {
    ...mapGetters("assignment", ["assignmentForm"]),
    ...mapGetters("auth", ["isAdmin", "isManager"]),
    // Staff read this tab: the note written for them, and whatever documents and
    // links were attached. The manager notes, the client's source text and the
    // controls for adding more are not theirs.
    canEditNotes() {
      return this.isAdmin || this.isManager;
    },
    // The assignment's staff-facing note (assignmentDetails.note) — the one note
    // field the record actually has.
    noteToStaff: {
      get() {
        return this.assignmentForm.note || '';
      },
      set(value) {
        this.updateAssignmentData({ field: 'note', data: value });
      }
    },
    documents() {
      return this.assignmentForm.documents || [];
    },
    // Stored files the user dropped in this session. ADForm detaches them on the
    // next save.
    removedDocuments() {
      return this.assignmentForm.removedDocuments || [];
    },
    links() {
      return this.assignmentForm.links || [];
    }
  },

  methods: {
    ...mapActions("assignment", ["updateAssignmentData"]),
    ...mapActions("flashMessage", ["handleFlash"]),
    accessTypeLabel(fileAccessType) {
      return getFileAccessTypeLabel(fileAccessType);
    },
    flashError(error) {
      this.handleFlash({
        response: { data: { statusCode: 500, error } },
        show: true
      });
    },
    normalizeLink(link) {
      return /^https?:\/\//i.test(link) ? link : `https://${link}`;
    },
    openDocumentsDialog() {
      this.stagedFiles = [];
      this.accessTypeToAdd = DEFAULT_FILE_ACCESS_TYPE;
      this.isDragOver = false;
      this.documentsDialog = true;
    },
    closeDocumentsDialog() {
      this.documentsDialog = false;
      this.stagedFiles = [];
      this.isDragOver = false;
    },
    browseFiles() {
      this.$refs.filePicker?.click();
    },
    onPick(event) {
      this.stageFiles(Array.from(event.target.files || []));
      // Reset the native input so picking the same file again still fires change.
      event.target.value = '';
    },
    onDrop(event) {
      this.isDragOver = false;
      this.stageFiles(Array.from(event.dataTransfer?.files || []));
    },
    // Files gather across every drop and every trip through the file picker;
    // the same file selected twice is only staged once.
    stageFiles(files) {
      if (!files.length) return;
      const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;
      const stagedKeys = new Set(this.stagedFiles.map(fileKey));
      const newFiles = files.filter((file) => {
        const key = fileKey(file);
        if (stagedKeys.has(key)) return false;
        stagedKeys.add(key);
        return true;
      });
      if (newFiles.length) this.stagedFiles = [...this.stagedFiles, ...newFiles];
    },
    removeStagedFile(index) {
      const stagedFiles = [...this.stagedFiles];
      stagedFiles.splice(index, 1);
      this.stagedFiles = stagedFiles;
    },
    clearStagedFiles() {
      this.stagedFiles = [];
    },
    formatFileSize(size) {
      if (!size) return '';
      const units = ['B', 'KB', 'MB', 'GB'];
      const unitIndex = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
      const value = size / Math.pow(1024, unitIndex);
      return `${unitIndex === 0 ? value : value.toFixed(1)} ${units[unitIndex]}`;
    },
    addDocuments() {
      if (!this.stagedFiles.length) return;
      // Held in the form only — the actual upload happens once the assignment
      // is saved (see ADForm.uploadPendingDocuments).
      const pendingDocuments = this.stagedFiles.map((file) =>
        createPendingDocument(file, this.accessTypeToAdd)
      );
      this.updateAssignmentData({
        field: 'documents',
        data: [...this.documents, ...pendingDocuments]
      });
      this.closeDocumentsDialog();
    },
    // A stored file is identified by its own id; one that is still only in the
    // form has none, so the row index stands in for it.
    documentKey(document, index) {
      return document.id
        ? `document-${document.id}`
        : `document-${index}-${document.fileName}`;
    },
    isDocumentBusy(document, index) {
      return this.busyDocuments.includes(this.documentKey(document, index));
    },
    async downloadDocument(document, index) {
      const key = this.documentKey(document, index);
      if (this.busyDocuments.includes(key)) return;
      this.busyDocuments = [...this.busyDocuments, key];
      try {
        const { ok, error } = await downloadAssignmentDocument(document);
        if (!ok) this.flashError(error);
      } catch (err) {
        console.error('Assignment document download error:', err);
        this.flashError(err.message || `Failed to download ${document.fileName}`);
      } finally {
        this.busyDocuments = this.busyDocuments.filter((el) => el !== key);
      }
    },
    // Drops the file from the form only. A file that is already stored is queued
    // on `removedDocuments` and detached by ADForm when the assignment is updated,
    // so the row disappearing marks the form dirty (the Update button appears) and
    // closing the form without saving puts the file back.
    removeDocument(document) {
      if (document.uploaded && document.id) {
        this.updateAssignmentData({
          field: 'removedDocuments',
          data: [...this.removedDocuments, document]
        });
      }
      this.updateAssignmentData({
        field: 'documents',
        data: this.documents.filter((el) => el !== document)
      });
    },
    openLinkDialog() {
      this.linkToAdd = '';
      this.linkError = '';
      this.linkDialog = true;
      this.$nextTick(() => this.$refs.linkInput?.focus());
    },
    closeLinkDialog() {
      this.linkDialog = false;
      this.linkToAdd = '';
      this.linkError = '';
    },
    addLink() {
      const link = this.linkToAdd.trim();
      if (!link) return;
      // Anything with a host is accepted (the scheme is optional and added when
      // the link is opened), so paths like dropbox.com/s/... work as typed.
      if (!/^(https?:\/\/)?[^\s./]+\.[^\s/]+/i.test(link)) {
        this.linkError = 'Enter a valid link';
        return;
      }
      if (this.links.includes(link)) {
        this.linkError = 'This link is already added';
        return;
      }
      this.updateAssignmentData({ field: 'links', data: [...this.links, link] });
      this.closeLinkDialog();
    }
  }
};
</script>

<style lang="scss" scoped>
.addFormNotesTab {
  &__delimetr {
    background-color: #0000001F;
    width: 100%;
    height: .5px;
    margin: 30px 0 15px 0;
  }
  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    padding: 0 8px;
    border-radius: 4px;
    &:hover {
      background-color: #F5F5F5;
      .addFormNotesTab__item--remove {
        visibility: visible;
      }
    }
    &--icon {
      color: #656F7D;
      flex-shrink: 0;
    }
    &--name {
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #1976D2;
      text-decoration: none;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    a.addFormNotesTab__item--name:hover {
      text-decoration: underline;
    }
    // A file name behaves like the link rows' anchor: clicking it downloads.
    &--name-action {
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
    &--meta {
      font-family: 'Roboto', sans-serif;
      font-size: 12px;
      color: #9E9E9E;
      white-space: nowrap;
    }
    // Same control as the Main tab's field clear buttons (item-input--clear):
    // hidden until the row is hovered, then click to drop the entry.
    &--remove {
      visibility: hidden;
      margin: 0 0 0 auto;
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      padding: 4px;
      border-radius: 2px;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      &:hover {
        background-color: #DEDEDE;
        cursor: pointer;
      }
    }
  }
  &__action {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 15px;
    margin-top: 15px;
    // Pinned to the foot of the scrolling panel. .assignment-form__tabs--content
    // clips with overflow-x: clip rather than hidden precisely so this resolves
    // against .scrollable-col and not against the tab panel's own box.
    position: sticky;
    bottom: 0;
    z-index: 3;
    background-color: #fff;
    padding: 12px 0;
    &--button {
      color: #fff;
    }
  }
  &__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 20px;
    border: 1px dashed #0000003D;
    border-radius: 4px;
    background-color: #FAFAFA;
    cursor: pointer;
    transition: border-color .2s ease, background-color .2s ease;
    &:hover,
    &--active {
      border-color: #1976D2;
      background-color: #1976D20D;
    }
    &--text {
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      color: #656F7D;
    }
    &--link {
      color: #1976D2;
      text-decoration: underline;
    }
    &--hint {
      font-family: 'Roboto', sans-serif;
      font-size: 12px;
      color: #9E9E9E;
    }
    &--input {
      display: none;
    }
  }
  &__staged {
    margin-top: 12px;
    &--header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-family: 'Roboto', sans-serif;
      font-size: 12px;
      font-weight: 500;
      color: #656F7D;
      margin-bottom: 4px;
    }
    &--list {
      max-height: 180px;
      overflow-y: auto;
      &::-webkit-scrollbar {
        width: 8px;
      }
      &::-webkit-scrollbar-track {
        background: #f1f1f1;
      }
      &::-webkit-scrollbar-thumb {
        background: #ADB3BD;
        border-radius: 8px;
      }
    }
    &--item {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 28px;
      padding: 0 6px;
      border-radius: 4px;
      &:hover {
        background-color: #F5F5F5;
      }
    }
    &--name {
      font-family: 'Roboto', sans-serif;
      font-size: 13px;
      color: #292A2C;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    &--size {
      font-family: 'Roboto', sans-serif;
      font-size: 11px;
      color: #9E9E9E;
      white-space: nowrap;
      margin-left: auto;
    }
    &--remove {
      flex-shrink: 0;
      align-self: center;
      width: 18px;
      height: 18px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      ::v-deep .v-btn__content,
      ::v-deep .v-icon {
        line-height: 1;
      }
    }
  }
  &__dialog {
    font-family: 'Roboto', sans-serif;
    border-radius: 10px !important;
    &--title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid #0000001F;
      & p {
        font-size: 16px;
        font-weight: 600;
        color: #656F7D;
        margin: 0;
      }
    }
    &--body {
      padding: 16px;
    }
    &--hint {
      font-size: 12px;
      color: #9E9E9E;
    }
    &--actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      padding: 0 16px 16px;
    }
  }
}
.mb-15px {
  margin-bottom: 15px;
}
</style>
